import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { RedirectFormFields, RedirectFormInputs, RedirectsQueryKeys, useEditRedirectsMutation, useGetByIdRedirectsQuery } from '@/modules/Sites';

import { RedirectsFormView } from './RedirectsFormView';
import { redirectFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  redirectId: string;
}

export const RedirectsEditFormContainer: FC<Props> = memo(({ siteId, redirectId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<RedirectFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof RedirectFormInputs>>(null);

  const {
    data: redirectData,
    error: redirectError,
    isLoading: isRedirectLoading,
    isError: isRedirectError,
    refetch: refetchRedirect
  } = useGetByIdRedirectsQuery(siteId, redirectId)({
    enabled: !!redirectId && !!siteId
  });

  const { mutate: editRedirectMutation, isLoading: editRedirectsIsLoading } =
  useEditRedirectsMutation(siteId, redirectId)({
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries([RedirectsQueryKeys.GetAll, siteId]),
        queryClient.refetchQueries([RedirectsQueryKeys.Conflicts, siteId]),
      ]);

      await refetchRedirect();

      appMessageApi.success('Редірект успішно змінено!');

      navigate(AppRoutes.RedirectsById.replace(':siteId', siteId).replace(':redirectId', redirectId));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof RedirectFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: RedirectFormFields = {
    forVisitor: redirectData?.forVisitor,
    isActive: redirectData?.isActive,
    sourceType: redirectData?.sourceType,
    sourceNodeId: redirectData?.sourceNodeId ?? undefined,
    sourcePath: redirectData?.sourcePath ?? undefined,
    targetType: redirectData?.targetType,
    targetNodeId: redirectData?.targetNodeId ?? undefined,
    targetPath: redirectData?.targetPath ?? undefined,
    statusCode: redirectData?.statusCode,
    matchMode: redirectData?.matchMode,
    siteId
  };

  const handleFinish = useCallback((values: RedirectFormFields): void => {
    setFormErrors(null);
    editRedirectMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.RedirectsById.replace(':siteId', siteId).replace(':redirectId', redirectId));
  }, [siteId, redirectId]);

  return (
    <FallbackedLayout
      error={ redirectError }
      isError={ isRedirectError }
      isLoading={ isRedirectLoading }
    >
      {
        () => (
          <RedirectsFormView
            formName="redirect-edit"
            formType="edit"
            form={ form }
            formErrors={ formErrors }
            initialValues={ initialValues }
            isFormLoading={ editRedirectsIsLoading }
            validationSchema={ redirectFormValidationSchema }
            handleBack={ handleBack }
            handleFinish={ handleFinish }
            handleFinishFailed={ handleFinishFailed }
          />
        )
      }
    </FallbackedLayout>
  );
});
